/**
 * Documents Async Thunks
 * All async actions for document operations
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import * as documentsApi from '../api/documentsApi';

/**
 * Fetch patient documents
 */
export const fetchPatientDocuments = createAsyncThunk(
  'documents/fetchPatientDocuments',
  async (patientId, { rejectWithValue }) => {
    try {
      return await documentsApi.fetchPatientDocuments(patientId);
    } catch (error) {
      console.error('Error fetching patient documents:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Set while a pending-approvals request started in the current synchronous run of code.
// Opening the EMR page asks twice in the same commit (the page's mount effect and the
// header's Documents button), and no write can land between two calls in one run, so
// the second call is dropped and both get the same answer from the store.
let pendingApprovalsAskedThisTurn = false;

/**
 * Fetch patients with pending approvals
 */
export const fetchPendingApprovals = createAsyncThunk(
  'documents/fetchPendingApprovals',
  async (_, { rejectWithValue }) => {
    try {
      return await documentsApi.fetchPendingApprovals();
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    condition: () => {
      if (pendingApprovalsAskedThisTurn) return false;
      pendingApprovalsAskedThisTurn = true;
      Promise.resolve().then(() => { pendingApprovalsAskedThisTurn = false; });
      return true;
    },
  }
);

/**
 * Fetch general pending documents
 */
export const fetchGeneralPendingDocuments = createAsyncThunk(
  'documents/fetchGeneralPendingDocuments',
  async (_, { rejectWithValue }) => {
    try {
      return await documentsApi.fetchGeneralPendingDocuments();
    } catch (error) {
      console.error('Error fetching general pending documents:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch documents for approval
 */
export const fetchDocumentsForApproval = createAsyncThunk(
  'documents/fetchDocumentsForApproval',
  async (patientId, { rejectWithValue }) => {
    try {
      return await documentsApi.fetchDocumentsForApproval(patientId);
    } catch (error) {
      console.error('Error fetching documents for approval:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Update document filename
 */
export const updateDocumentFilename = createAsyncThunk(
  'documents/updateDocumentFilename',
  async ({ patientId, fileId, fileName }, { rejectWithValue }) => {
    try {
      await documentsApi.updateDocumentFilename(patientId, fileId, fileName);
      return { patientId, fileId, fileName };
    } catch (error) {
      console.error('Error updating document filename:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Approve document
 */
export const approveDocument = createAsyncThunk(
  'documents/approveDocument',
  async ({ patientId, fileId }, { rejectWithValue }) => {
    try {
      await documentsApi.approveDocument(patientId, fileId);
      return { patientId, fileId };
    } catch (error) {
      console.error('Error approving document:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Reject document
 */
export const rejectDocument = createAsyncThunk(
  'documents/rejectDocument',
  async ({ patientId, fileId, reason }, { rejectWithValue }) => {
    try {
      await documentsApi.rejectDocument(patientId, fileId, reason);
      return { patientId, fileId };
    } catch (error) {
      console.error('Error rejecting document:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Update document submission status
 */
export const updateDocumentSubmissionStatus = createAsyncThunk(
  'documents/updateDocumentSubmissionStatus',
  async ({ patientId, isSubmitted }, { rejectWithValue }) => {
    try {
      await documentsApi.updateDocumentSubmissionStatus(patientId, isSubmitted);
      return { patientId, isSubmitted };
    } catch (error) {
      console.error('Error updating submission status:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
